import { useEffect, useState } from "react";
import ApiService from "../../core/service/api-service";
import { Button, Empty, Form, Modal, Spin, Tag, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { dateFormat } from "../../core/utils/date-utils";
import { CheckCircleOutlined, HistoryOutlined, LoadingOutlined } from "@ant-design/icons";
import CompactCardList from "../../core/components/CompactCardList";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import TaskResolutionField from "./TaskResolutionField";

// Tek seferde çekilen kayıt sayısı — bkz. StudyLogsGrid: liste sayfalanmıyor, kaydırılıyor.
const TASK_FETCH_SIZE = 100;

const StudyTaskTypeEnum = {
    1: 'MISSING_IMAGE',
    2: 'RE_ASSESSMENT',
    3: 'REMOVE_REPORT_CONFIRMATION'
}

const StudyTaskStatusEnum = {
    1: 'OPEN',
    2: 'RESOLVED',
    3: 'CANCEL'
}

const getStatusColor = (status) => {
    switch (status) {
        case 1: return 'orange'; // OPEN
        case 2: return 'green';  // RESOLVED
        case 3: return 'red';    // CANCEL
        default: return 'default';
    }
}

export default function StudyTasksGrid({ studyId }) {
    const { t } = useTranslation();
    const { hasPermission } = useUser();
    // Kapatma yetkisi sunucudaki kuralin aynisi (bkz. StudyTaskService.Resolve): salt-goruntuleme
    // izniyle gelen kullaniciya butonu gostermek, basildiginda "Not Allowed" demekten baska ise yaramaz.
    const canResolve = hasPermission(PERMISSIONS.STUDY_TASK_MANAGER) || hasPermission(PERMISSIONS.REPORT_CONFIRM);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [resolveModalVisible, setResolveModalVisible] = useState(false);
    const [detailTask, setDetailTask] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [form] = Form.useForm();

    // Yükleme göstergesi listenin İÇİNDE (silent: küresel gösterge susar) — bkz. StudyRequestGrid.
    const load = async () => {
        setLoading(true);
        try {
            const response = await ApiService.StudyTask.DataGrid({
                filter: { studyId: studyId },
                paginationQuery: { pageNumber: 1, pageSize: TASK_FETCH_SIZE },
                orderByItems: []
            }, { silent: true });

            if (response.success)
                setTasks(response.data || []);
        }
        finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyId]);

    const handleResolve = async () => {
        try {
            const values = await form.validateFields();
            const response = await ApiService.StudyTask.Resolve({
                taskId: selectedTask.id,
                description: values.description
            });
            if (response.success) {
                setResolveModalVisible(false);
                setSelectedTask(null);
                form.resetFields();
                load();
            }
        } catch (error) {
            console.error('Validation failed:', error);
        }
    }

    // Görevin "JSON" karşılığı: açıklama + durum geçmişi tek modalda.
    const openDetail = async (task) => {
        setDetailTask(task);
        setHistoryData([]);
        setHistoryLoading(true);
        try {
            const response = await ApiService.StudyTask.GetHistory(task.id);
            if (response.success)
                setHistoryData(response.data || []);
        } catch (error) {
            console.error('Failed to load history:', error);
        } finally {
            setHistoryLoading(false);
        }
    }

    return (
        <>
            <CompactCardList
                items={tasks}
                loading={loading}
                rowKey={'id'}
                minVisibleCards={5}
                onCardClick={openDetail}
                card={(record) => ({
                    title: (
                        <>
                            <Tag color={getStatusColor(record.status)}>
                                {t(StudyTaskStatusEnum[record.status] || record.status)}
                            </Tag>
                            {t(StudyTaskTypeEnum[record.taskType] || record.taskType)}
                        </>
                    ),
                    extra: (
                        <>
                            {record.status === 1 && canResolve && (
                                <Tooltip title={t('Resolve')}>
                                    <Button
                                        type={'primary'}
                                        size={'small'}
                                        icon={<CheckCircleOutlined />}
                                        onClick={() => {
                                            setSelectedTask(record);
                                            setResolveModalVisible(true);
                                        }}
                                    />
                                </Tooltip>
                            )}
                            <Tooltip title={t('History')}>
                                <Button
                                    size={'small'}
                                    icon={<HistoryOutlined />}
                                    onClick={() => openDetail(record)}
                                />
                            </Tooltip>
                        </>
                    ),
                    subLeft: dateFormat(record.createdAt),
                    subRight: record.user?.visibleName,
                    tooltip: record.description
                })}
            />

            <Modal
                title={t('Resolve Task')}
                open={resolveModalVisible}
                onCancel={() => {
                    setResolveModalVisible(false);
                    setSelectedTask(null);
                    form.resetFields();
                }}
                onOk={handleResolve}
                okText={t('Resolve')}
                cancelText={t('Cancel')}
            >
                {/* Yeniden değerlendirme serbest metinle kapanmaz; sunucu iki sabit sonuçtan
                    birini bekliyor (bkz. StudyTaskService.Resolve). Diğer tiplerde serbest metin. */}
                <Form form={form} layout="vertical">
                    <TaskResolutionField tasks={selectedTask ? [selectedTask] : []} />
                </Form>
            </Modal>

            <Modal
                title={detailTask ? t(StudyTaskTypeEnum[detailTask.taskType] || detailTask.taskType) : t('Task History')}
                open={!!detailTask}
                onCancel={() => {
                    setDetailTask(null);
                    setHistoryData([]);
                }}
                footer={null}
                width={640}
            >
                {detailTask?.description && (
                    <div style={{
                        marginBottom: 12,
                        padding: '8px 12px',
                        background: '#fafafa',
                        border: '1px solid #f0f0f0',
                        borderRadius: 4
                    }}>
                        <Typography.Text type={'secondary'} style={{ fontSize: 11 }}>
                            {t('Description')}
                        </Typography.Text>
                        <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {detailTask.description}
                        </div>
                    </div>
                )}

                <Typography.Text type={'secondary'} style={{ fontSize: 11 }}>
                    {t('Task History')}
                </Typography.Text>

                {/* Geçmiş de aynı compact kart düzeninde — modal dar ekranda da yatay scroll'a düşmesin. */}
                {historyLoading
                    ? (
                        <div style={{ textAlign: 'center', padding: 16 }}>
                            <Spin indicator={<LoadingOutlined spin />} size={'small'} />
                        </div>
                    )
                    : historyData.length === 0
                        ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No Data')} />
                        : (
                            <div style={{ marginTop: 6, maxHeight: '50vh', overflow: 'auto' }}>
                                <CompactCardList
                                    items={historyData}
                                    rowKey={'id'}
                                    minVisibleCards={0}
                                    card={(item) => ({
                                        title: (
                                            <>
                                                <Tag color={getStatusColor(item.oldStatus)}>
                                                    {t(StudyTaskStatusEnum[item.oldStatus] || item.oldStatus)}
                                                </Tag>
                                                →
                                                <Tag color={getStatusColor(item.newStatus)}>
                                                    {t(StudyTaskStatusEnum[item.newStatus] || item.newStatus)}
                                                </Tag>
                                            </>
                                        ),
                                        subLeft: dateFormat(item.createdAt),
                                        subRight: item.user?.visibleName,
                                        tooltip: item.description
                                    })}
                                />
                            </div>
                        )}
            </Modal>
        </>
    )
}
